import type { DeviceToolState } from '@/composables/equipment/useDeviceTools'
import type { DeviceRebootResult } from '@/types/api-client/device-tools'

/**
 * Normalizes DeviceToolState into a unified reboot result object
 * Provides a single source of truth for all reboot state
 * Similar to how DeviceStatus normalizes array responses
 *
 * @param state - The device tool state from useDeviceTools
 * @returns Normalized reboot result object
 *
 * @example
 * const rebootResult = computed(() =>
 *   normalizeRebootResult(state.value)
 * )
 */
export function normalizeRebootResult(
	state: DeviceToolState | null | undefined
): DeviceRebootResult {
	if (!state) {
		return {
			isLoading: false,
			success: false,
			hasError: false,
			error: null,
			progress: 0,
			finished: false,
			cooldownSeconds: 0,
			severity: 'default'
		}
	}

	const hasError = !!state.error
	const success = state.finished && !hasError && !!state.result
	const isLoading = state.loading

	// Derive severity from state
	let severity: 'ok' | 'error' | 'loading' | 'default'
	if (isLoading) {
		severity = 'loading'
	}
	else if (success) {
		severity = 'ok'
	}
	else if (hasError) {
		severity = 'error'
	}
	else {
		severity = 'default'
	}

	return {
		isLoading,
		success,
		hasError,
		error: state.error,
		progress: state.progress ?? 0,
		finished: state.finished,
		cooldownSeconds: state.cooldownSeconds ?? 0,
		severity
	}
}
